<template>
  <div
    class="external-form"
    :class="{ light: switchValue, dark: !switchValue }"
  >
    <ExternalFormHeader
      :clientName="clientName"
      :language="language"
      :languagesTV="languagesTV"
      :submitButtonFlag="submitButtonFlag"
      @switchValueChanged="onChangeSwitchValue"
      @languageChanged="onLanguageChange"
      @submit="onBookingRequestSubmit"
    >
      <template #pageTitle>
        <span class="page-titile">Booking Request Form </span>
      </template>
    </ExternalFormHeader>

    <div class="container">
      <div class="exform-row">
        <div class="exform-column">
          <div
            class="dt-modal--subtitle ut-mt-0 ut-mb-10px ut-text-secondary ut-font-capitalize"
          >
            Event infos
          </div>

          <BasicAccordion
            v-if="(bookingRequest && bookingRequest.rs) != null"
            :accortionTitle="$t('event_details')"
            :validationIconName="
              Object.values(this.eventDetailsValidationResult).every(
                (e) => e.valid
              )
                ? 'textbox-complete.svg'
                : 'textbox-incomplete.svg'
            "
            :class="{ opened: openedState }"
            :expandState="event_detailsExpandState"
            @clicked="(e) => onShowAccordion('event_details', e)"
          >
            <div
              class="form-group"
              v-if="
                bookingRequest &&
                bookingRequest.rs &&
                bookingRequest.rs.calendar_date
              "
            >
              <TextField
                v-if="calendarDateSettings('Date')"
                type="date"
                :id="'booking_start_date'"
                class="m1"
                :placeholder="$t('Date')"
                :value="bookingRequest.booking_start_date"
                :showImmediate="true"
                autocomplete="off"
                @valuechanged="onDateChange"
                @validated="onEventDetailsValidation"
                :required="calendarDateSettingsMandatory('Date')"
                :showValidationIcons="calendarDateSettingsMandatory('Date')"
                immediateValidation
              />
              <TextField
                v-if="calendarDateSettings('Event Name')"
                :id="'booking_date_name'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.booking_date_name"
                :placeholder="$t('rs_event_name')"
                @valuechanged="onEventDetailsTextFieldValueChange"
                @validated="onEventDetailsValidation"
                :required="calendarDateSettingsMandatory('Event Name')"
                :showValidationIcons="
                  calendarDateSettingsMandatory('Event Name')
                "
                immediateValidation
              />

              <TextField
                v-if="calendarDateSettings('Event URL')"
                :id="'booking_event_url'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.booking_event_url"
                :placeholder="$t('event_url')"
                @valuechanged="onEventDetailsTextFieldValueChange"
                @validated="onEventDetailsValidation"
                :required="calendarDateSettingsMandatory('Event URL')"
                :showValidationIcons="
                  calendarDateSettingsMandatory('Event URL')
                "
                immediateValidation
              />

              <TextField
                v-if="calendarDateSettings('Line Up')"
                :id="'booking_lineup'"
                class="m1"
                v-bind="textAreaConfig"
                :value="bookingRequest.booking_lineup"
                :placeholder="$dft.snakeCaseToWords('timetable')"
                :height="80"
                @valuechanged="onEventDetailsTextFieldValueChange"
                @validated="onEventDetailsValidation"
                :required="calendarDateSettingsMandatory('Line Up')"
                :showValidationIcons="calendarDateSettingsMandatory('Line Up')"
                immediateValidation
              />

              <TextField
                v-if="calendarDateSettings('Doors')"
                :id="'booking_doors_open'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.booking_doors_open"
                :placeholder="$t('doors_open')"
                @valuechanged="onEventDetailsTextFieldValueChange"
                @validated="onEventDetailsValidation"
                :required="calendarDateSettingsMandatory('Doors')"
                :showValidationIcons="calendarDateSettingsMandatory('Doors')"
                immediateValidation
              />

              <TextField
                v-if="calendarDateSettings('Curfew')"
                :id="'booking_curfew'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.booking_curfew"
                :placeholder="$t('curfew')"
                @valuechanged="onEventDetailsTextFieldValueChange"
                @validated="onEventDetailsValidation"
                :required="calendarDateSettingsMandatory('Curfew')"
                :showValidationIcons="calendarDateSettingsMandatory('Curfew')"
                immediateValidation
              />

              <div
                v-for="eventField of bookingRequest.calendar_date_cf"
                :key="eventField.custom_field_id"
              >
                <TextField
                  v-if="calendarDateSettings(eventField.field_label)"
                  :id="eventField.field_label"
                  class="m1"
                  v-bind="textFieldConfig"
                  :value="eventField.data"
                  :placeholder="`${$dft.capSnake(eventField.field_label)}`"
                  @valuechanged="
                    (name, value) =>
                      onEventFieldsUpdate(name, value, eventField)
                  "
                  @validated="
                    (name, value) =>
                      onEventFieldValidation(name, value, eventField)
                  "
                  :required="
                    calendarDateSettingsMandatory(eventField.field_label)
                  "
                  :showValidationIcons="
                    calendarDateSettingsMandatory(eventField.field_label)
                  "
                  immediateValidation
                />
              </div>
            </div>
          </BasicAccordion>

          <BasicAccordion
            v-if="(bookingRequest && bookingRequest.rs) != null"
            :accortionTitle="$t('artist_details')"
            :validationIconName="
              isArtistAccordionValidated
                ? 'textbox-complete.svg'
                : 'textbox-incomplete.svg'
            "
            :expandState="artist_detailsExpandState"
            @clicked="(e) => onShowAccordion('artist_details', e)"
          >
            <div
              v-if="
                bookingRequest &&
                bookingRequest.rs &&
                bookingRequest.rs.calendar_date
              "
            >
              <div
                class="form-group"
                v-for="(artist, index) in bookingRequest.artistsList"
                :index="index"
                :key="index"
              >
                <div class="ut-rowcen ut-mt-10px" v-if="index > 0">
                  <span class="ut-flex">
                    <span>{{ $t("artist") }} {{ index + 1 }}</span>
                    <img
                      src="static/icons/delete.svg"
                      @click.stop="(e) => onDeleteArtist(index, artist)"
                      class="dt-icon v5 s1 ut-center"
                      alt
                    />
                  </span>
                </div>
                <Dropdown
                  class="m2"
                  v-bind="requiredDropdownConfig"
                  :placeholder="$t('artists')"
                  :options="artistsTV"
                  :selected="artistsTV.find((e) => e.value == artist.artist_id)"
                  @selectedChange="
                    (value) => onArtistSelection(value, artist, index)
                  "
                  box
                  @validated="
                    (e) =>
                      onArtistDropdownValidation('artist_id', e, index, artist)
                  "
                  :required="true"
                  :showValidationIcons="true"
                  immediateValidation
                ></Dropdown>

                <TextField
                  v-if="calendarDateArtistSettings('Offer')"
                  class="m1"
                  :id="'first_offer_notes'"
                  v-bind="textFieldConfig"
                  :placeholder="$t('first_offer')"
                  :value="artist.first_offer_notes"
                  @valuechanged="
                    (name, value) =>
                      onArtistTextFieldValueChange(name, value, index)
                  "
                  @validated="
                    (name, value) =>
                      onArtistTextFieldValidation(name, value, index, artist)
                  "
                  :required="calendarDateArtistSettingsMandatory('Offer')"
                  :showValidationIcons="
                    calendarDateArtistSettingsMandatory('Offer')
                  "
                  immediateValidation
                />
                <!-- v-if="artist.performance_time != ''" -->
                <TextField
                  v-if="calendarDateArtistSettings('Duration')"
                  class="m1"
                  :id="'performance_time'"
                  v-bind="textFieldConfig"
                  :placeholder="$t('pfm_time')"
                  :value="artist.performance_time"
                  @valuechanged="
                    (name, value) =>
                      onArtistTextFieldValueChange(name, value, index)
                  "
                  @validated="
                    (name, value) =>
                      onArtistTextFieldValidation(name, value, index, artist)
                  "
                  :required="calendarDateArtistSettingsMandatory('Duration')"
                  :showValidationIcons="
                    calendarDateArtistSettingsMandatory('Duration')
                  "
                  immediateValidation
                />
                <TextField
                  v-if="calendarDateArtistSettings('Showtime')"
                  class="m1"
                  :id="'performance_time_contract'"
                  v-bind="textFieldConfig"
                  :placeholder="$t('playtime')"
                  :value="artist.performance_time_contract"
                  @valuechanged="
                    (name, value) =>
                      onArtistTextFieldValueChange(name, value, index)
                  "
                  @validated="
                    (name, value) =>
                      onArtistTextFieldValidation(name, value, index, artist)
                  "
                  :required="calendarDateArtistSettingsMandatory('Showtime')"
                  :showValidationIcons="
                    calendarDateArtistSettingsMandatory('Showtime')
                  "
                  immediateValidation
                />
                <TextField
                  v-if="calendarDateArtistSettings('Soundcheck')"
                  class="m1"
                  :id="'artist_soundcheck'"
                  v-bind="textFieldConfig"
                  :placeholder="$t('soundcheck')"
                  :value="artist.artist_soundcheck"
                  @valuechanged="
                    (name, value) =>
                      onArtistTextFieldValueChange(name, value, index)
                  "
                  @validated="
                    (name, value) =>
                      onArtistTextFieldValidation(name, value, index, artist)
                  "
                  :required="calendarDateArtistSettingsMandatory('Soundcheck')"
                  :showValidationIcons="
                    calendarDateArtistSettingsMandatory('Soundcheck')
                  "
                  immediateValidation
                />
                <TextField
                  v-if="calendarDateArtistSettings('Artist Before')"
                  class="m1"
                  :id="'artist_before'"
                  v-bind="textFieldConfig"
                  :placeholder="$t('artist_before')"
                  :value="artist.artist_before"
                  @valuechanged="
                    (name, value) =>
                      onArtistTextFieldValueChange(name, value, index)
                  "
                  @validated="
                    (name, value) =>
                      onArtistTextFieldValidation(name, value, index, artist)
                  "
                  :required="
                    calendarDateArtistSettingsMandatory('Artist Before')
                  "
                  :showValidationIcons="
                    calendarDateArtistSettingsMandatory('Artist Before')
                  "
                  immediateValidation
                />
                <TextField
                  v-if="calendarDateArtistSettings('Artist After')"
                  class="m1"
                  :id="'artist_after'"
                  v-bind="textFieldConfig"
                  :placeholder="$t('artist_after')"
                  :value="artist.artist_after"
                  @valuechanged="
                    (name, value) =>
                      onArtistTextFieldValueChange(name, value, index)
                  "
                  @validated="
                    (name, value) =>
                      onArtistTextFieldValidation(name, value, index, artist)
                  "
                  :required="
                    calendarDateArtistSettingsMandatory('Artist After')
                  "
                  :showValidationIcons="
                    calendarDateArtistSettingsMandatory('Artist After')
                  "
                  immediateValidation
                />
                <TextField
                  v-if="calendarDateArtistSettings('Stage Floor')"
                  class="m1"
                  :id="'artist_stage_floor'"
                  v-bind="textFieldConfig"
                  :placeholder="$t('stage_floor')"
                  :value="artist.artist_stage_floor"
                  @valuechanged="
                    (name, value) =>
                      onArtistTextFieldValueChange(name, value, index)
                  "
                  @validated="
                    (name, value) =>
                      onArtistTextFieldValidation(name, value, index, artist)
                  "
                  :required="calendarDateArtistSettingsMandatory('Stage Floor')"
                  :showValidationIcons="
                    calendarDateArtistSettingsMandatory('Stage Floor')
                  "
                  immediateValidation
                />

                <div
                  v-for="artistField of bookingRequest.calendar_date_artist_cf"
                  :key="artistField.custom_field_id"
                >
                  <TextField
                    v-if="calendarDateArtistSettings(artistField.field_label)"
                    :id="artistField.field_label"
                    class="m1"
                    v-bind="textFieldConfig"
                    :value="artistField.data"
                    :placeholder="`${$dft.capSnake(artistField.field_label)}`"
                    @valuechanged="
                      (name, value) =>
                        onArtistFieldsUpdate(name, value, artistField)
                    "
                    @validated="
                      (name, value) =>
                        onArtistFieldValidation(name, value, index)
                    "
                    :required="
                      calendarDateArtistSettingsMandatory(
                        artistField.field_label
                      )
                    "
                    :showValidationIcons="
                      calendarDateArtistSettingsMandatory(
                        artistField.field_label
                      )
                    "
                    immediateValidation
                  />
                </div>
              </div>
            </div>

            <div class="ut-rowcen ut-mt-10px">
              <button
                class="dt-button v1 s6 ut-m-0"
                @click.stop="onAddArtist()"
              >
                {{ $t("add") }} {{ $t("artist") }}
              </button>
            </div>
          </BasicAccordion>

          <BasicAccordion
            v-if="(bookingRequest && bookingRequest.rs) != null"
            :accortionTitle="$t('contact')"
            :validationIconName="
              Object.values(this.contactValidationResult).every((e) => e.valid)
                ? 'textbox-complete.svg'
                : 'textbox-incomplete.svg'
            "
            :expandState="contactExpandState"
            @clicked="(e) => onShowAccordion('contact', e)"
          >
            <div
              class="form-group"
              v-if="
                bookingRequest && bookingRequest.rs && bookingRequest.rs.contact
              "
            >
              <TextField
                v-if="contactSettings('First Name')"
                :id="'contact_first_name'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_first_name"
                :placeholder="$t('first_name')"
                @valuechanged="onContactTextFieldValueChange"
                :required="true"
                :showValidationIcons="true"
                immediateValidation
                @validated="onContactValidation"
              />
              <TextField
                v-if="contactSettings('Last Name')"
                :id="'contact_last_name'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_last_name"
                :placeholder="$t('last_name')"
                @valuechanged="onContactTextFieldValueChange"
                :required="true"
                :showValidationIcons="true"
                immediateValidation
                @validated="onContactValidation"
              />
              <TextField
                v-if="contactSettings('Address1')"
                :id="'contact_address1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_address1"
                :placeholder="$t('address')"
                @valuechanged="onContactTextFieldValueChange"
                @validated="onContactValidation"
                :required="contactSettingsMandatory('Address1')"
                :showValidationIcons="contactSettingsMandatory('Address1')"
                immediateValidation
              />
              <TextField
                v-if="contactSettings('Address2')"
                :id="'contact_address2'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_address2"
                :placeholder="$t('address_2')"
                @valuechanged="onContactTextFieldValueChange"
                @validated="onContactValidation"
                :required="contactSettingsMandatory('Address2')"
                :showValidationIcons="contactSettingsMandatory('Address2')"
                immediateValidation
              />
              <TextField
                v-if="contactSettings('City')"
                :id="'contact_city'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_city"
                :placeholder="$t('city')"
                @valuechanged="onContactTextFieldValueChange"
                @validated="onContactValidation"
                :required="contactSettingsMandatory('City')"
                :showValidationIcons="contactSettingsMandatory('City')"
                immediateValidation
              />
              <TextField
                v-if="contactSettings('Postalcode')"
                :id="'contact_postalcode'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_postalcode"
                :placeholder="$t('postalcode')"
                @valuechanged="onContactTextFieldValueChange"
                @validated="onContactValidation"
                :required="contactSettingsMandatory('Postalcode')"
                :showValidationIcons="contactSettingsMandatory('Postalcode')"
                immediateValidation
              />
              <Dropdown
                v-if="contactSettings('Country')"
                class="m2"
                box
                v-bind="requiredDropdownConfig"
                :placeholder="$t('country')"
                :options="countriesTV"
                :selected="
                  countriesTV.find(
                    (e) => e.value == bookingRequest.contact_country
                  )
                "
                @selectedChange="(e) => onCountryChange('contact_country', e)"
                @validated="(e) => onContactValidation('contact_country', e)"
                :required="contactSettingsMandatory('Country')"
                :showValidationIcons="contactSettingsMandatory('Country')"
                immediateValidation
              ></Dropdown>
              <TextField
                v-if="contactSettings('Phone1')"
                :id="'contact_phone1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_phone1"
                :placeholder="$t('phone')"
                @valuechanged="onContactTextFieldValueChange"
                @validated="onContactValidation"
                :required="contactSettingsMandatory('Phone1')"
                :showValidationIcons="contactSettingsMandatory('Phone1')"
                immediateValidation
              />
              <TextField
                v-if="contactSettings('Phone2')"
                :id="'contact_phone2'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_phone2"
                :placeholder="$t('mobile')"
                @valuechanged="onContactTextFieldValueChange"
                @validated="onContactValidation"
                :required="contactSettingsMandatory('Phone2')"
                :showValidationIcons="contactSettingsMandatory('Phone2')"
                immediateValidation
              />
              <TextField
                v-if="contactSettings('Email1')"
                :id="'contact_email1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_email1"
                :placeholder="$t('email')"
                @valuechanged="onContactTextFieldValueChange"
                @validated="onContactValidation"
                :required="contactSettingsMandatory('Email1')"
                :showValidationIcons="contactSettingsMandatory('Email1')"
                immediateValidation
              />
              <TextField
                v-if="contactSettings('Fax1')"
                :id="'contact_fax1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_fax1"
                :placeholder="$t('fax')"
                @valuechanged="onContactTextFieldValueChange"
                @validated="onContactValidation"
                :required="contactSettingsMandatory('Fax1')"
                :showValidationIcons="contactSettingsMandatory('Fax1')"
                immediateValidation
              />
              <TextField
                v-if="contactSettings('Skype')"
                :id="'contact_chat3'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.contact_chat3"
                :placeholder="$t('skype')"
                @valuechanged="onContactTextFieldValueChange"
                @validated="onContactValidation"
                :required="contactSettingsMandatory('Skype')"
                :showValidationIcons="contactSettingsMandatory('Skype')"
                immediateValidation
              />

              <div
                v-for="contactField of bookingRequest.contact_cf"
                :key="contactField.custom_field_id"
              >
                <TextField
                  v-if="contactSettings(contactField.field_label)"
                  :id="contactField.field_label"
                  class="m1"
                  v-bind="textFieldConfig"
                  :value="contactField.data"
                  :placeholder="`${$dft.capSnake(contactField.field_label)}`"
                  @valuechanged="
                    (name, value) =>
                      onContactFieldsUpdate(name, value, contactField)
                  "
                  @validated="
                    (name, value) =>
                      onContactFieldValidation(name, value, contactField)
                  "
                  :required="contactSettingsMandatory(contactField.field_label)"
                  :showValidationIcons="
                    contactSettingsMandatory(contactField.field_label)
                  "
                  immediateValidation
                />
              </div>
            </div>
          </BasicAccordion>

          <!-- v-if="bookingRequest != null" -->
          <BasicAccordion
            v-if="(bookingRequest && bookingRequest.rs) != null"
            :accortionTitle="$t('contract_partner_details')"
            :validationIconName="
              Object.values(this.promoterValidationResult).every((e) => e.valid)
                ? 'textbox-complete.svg'
                : 'textbox-incomplete.svg'
            "
            :expandState="contract_partner_detailsExpandState"
            @clicked="(e) => onShowAccordion('contract_partner_details', e)"
          >
            <div
              class="form-group"
              v-if="
                bookingRequest &&
                bookingRequest.rs &&
                bookingRequest.rs.promoter
              "
            >
              <TextField
                v-if="promoterSettings('Promoter Name')"
                :id="'promoter_company_name'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_company_name"
                :placeholder="$t('company_name')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="true"
                :showValidationIcons="true"
                immediateValidation
              />

              <TextField
                v-if="promoterSettings('Legal / Representative')"
                :id="'promoter_company_legalname'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_company_legalname"
                :placeholder="$t('legal_name')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('Legal / Representative')"
                :showValidationIcons="
                  promoterSettingsMandatory('Legal / Representative')
                "
                immediateValidation
              />

              <TextField
                v-if="promoterSettings('Address1')"
                :id="'promoter_address1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_address1"
                :placeholder="$t('address')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('Address1')"
                :showValidationIcons="promoterSettingsMandatory('Address1')"
                immediateValidation
              />
              <TextField
                v-if="promoterSettings('Address2')"
                :id="'promoter_address2'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_address2"
                :placeholder="$t('address_2')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('Address2')"
                :showValidationIcons="promoterSettingsMandatory('Address2')"
                immediateValidation
              />

              <TextField
                v-if="promoterSettings('City')"
                :id="'promoter_city'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_city"
                :placeholder="$t('city')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('City')"
                :showValidationIcons="promoterSettingsMandatory('City')"
                immediateValidation
              />
              <TextField
                v-if="promoterSettings('Postalcode')"
                :id="'promoter_postalcode'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_postalcode"
                :placeholder="$t('postal_code')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('Postalcode')"
                :showValidationIcons="promoterSettingsMandatory('Postalcode')"
                immediateValidation
              />
              <Dropdown
                v-if="promoterSettings('Country')"
                class="m2"
                box
                v-bind="requiredDropdownConfig"
                :placeholder="$t('country')"
                :options="countriesTV"
                :selected="
                  countriesTV.find(
                    (e) => e.value == bookingRequest.promoter_country
                  )
                "
                @selectedChange="(e) => onCountryChange('promoter_country', e)"
                @validated="
                  (e) => onPromoterDetailsValidation('promoter_country', e)
                "
                :required="promoterSettingsMandatory('Country')"
                :showValidationIcons="promoterSettingsMandatory('Country')"
                immediateValidation
              ></Dropdown>

              <TextField
                v-if="promoterSettings('Phone1')"
                :id="'promoter_phone1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_phone1"
                :placeholder="$t('phone')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('Phone1')"
                :showValidationIcons="promoterSettingsMandatory('Phone1')"
                immediateValidation
              />
              <TextField
                v-if="promoterSettings('Phone2')"
                :id="'promoter_phone2'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_phone2"
                :placeholder="$t('mobile')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('Phone2')"
                :showValidationIcons="promoterSettingsMandatory('Phone2')"
                immediateValidation
              />
              <TextField
                v-if="promoterSettings('Email')"
                :id="'promoter_email1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_email1"
                :placeholder="$t('email')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('Email')"
                :showValidationIcons="promoterSettingsMandatory('Email')"
                immediateValidation
              />
              <TextField
                v-if="promoterSettings('VAT ID')"
                :id="'promoter_vat_no'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_vat_no"
                :placeholder="$t('vat_id')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('VAT ID')"
                :showValidationIcons="promoterSettingsMandatory('VAT ID')"
                immediateValidation
              />
              <TextField
                v-if="promoterSettings('Tax No')"
                :id="'promoter_tax_no'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.promoter_tax_no"
                :placeholder="$t('tax_id')"
                @valuechanged="onPromoterTextFieldValueChange"
                @validated="onPromoterDetailsValidation"
                :required="promoterSettingsMandatory('Tax No')"
                :showValidationIcons="promoterSettingsMandatory('Tax No')"
                immediateValidation
              />

              <div
                v-for="promoterField of bookingRequest.promoter_cf"
                :key="promoterField.custom_field_id"
              >
                <TextField
                  v-if="promoterSettings(promoterField.field_label)"
                  :id="promoterField.field_label"
                  class="m1"
                  v-bind="textFieldConfig"
                  :value="promoterField.field_label"
                  :placeholder="`${$dft.capSnake(promoterField.field_label)}`"
                  @valuechanged="
                    (name, value) =>
                      onPromoterFieldsUpdate(name, value, promoterField)
                  "
                  @validated="
                    (name, value) =>
                      onPromoterFieldValidation(name, value, promoterField)
                  "
                  :required="
                    promoterSettingsMandatory(promoterField.field_label)
                  "
                  :showValidationIcons="
                    promoterSettingsMandatory(promoterField.field_label)
                  "
                  immediateValidation
                />
              </div>
            </div>
          </BasicAccordion>
        </div>

        <div class="exform-column">
          <div
            class="dt-modal--subtitle ut-mt-0 ut-mb-10px ut-text-secondary ut-font-capitalize"
          >
            &nbsp;
          </div>
          <BasicAccordion
            v-if="(bookingRequest && bookingRequest.rs) != null"
            :accortionTitle="$t('location_details')"
            :validationIconName="
              Object.values(this.venueValidationResult).every((e) => e.valid)
                ? 'textbox-complete.svg'
                : 'textbox-incomplete.svg'
            "
            :expandState="location_detailsExpandState"
            @clicked="(e) => onShowAccordion('location_details', e)"
          >
            <div
              class="form-group"
              v-if="
                bookingRequest &&
                bookingRequest.rs &&
                bookingRequest.rs.booking_location
              "
            >
              <TextField
                v-if="bookingLocationSettings('Venue Name')"
                :id="'venue_name'"
                class="m1"
                v-bind="typeAheadTextFieldConfig"
                :placeholder="$t('location_name')"
                :value="bookingRequest.venue_name"
                :options="venueTV"
                @selected="onVenueSelected"
                @valuechanged="onVenueTextFieldValueChange"
                :required="true"
                :showValidationIcons="true"
                immediateValidation
              />
              <TextField
                v-if="bookingLocationSettings('Address1')"
                :id="'venue_address1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.venue_address1"
                :placeholder="$t('address')"
                @valuechanged="onVenueTextFieldValueChange"
                @validated="onVenueDetailsValidation"
                :required="bookingLocationSettingsMandatory('Address1')"
                :showValidationIcons="
                  bookingLocationSettingsMandatory('Address1')
                "
                immediateValidation
              />
              <TextField
                v-if="bookingLocationSettings('Address2')"
                :id="'venue_address2'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.venue_address2"
                :placeholder="$t('address_2')"
                @valuechanged="onVenueTextFieldValueChange"
                @validated="onVenueDetailsValidation"
                :required="bookingLocationSettingsMandatory('Address2')"
                :showValidationIcons="
                  bookingLocationSettingsMandatory('Address2')
                "
                immediateValidation
              />
              <TextField
                v-if="bookingLocationSettings('City')"
                :id="'venue_city'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.venue_city"
                :placeholder="$t('city')"
                @valuechanged="onVenueTextFieldValueChange"
                @validated="onVenueDetailsValidation"
                :required="bookingLocationSettingsMandatory('City')"
                :showValidationIcons="bookingLocationSettingsMandatory('City')"
                immediateValidation
              />
              <TextField
                v-if="bookingLocationSettings('Postalcode')"
                :id="'venue_postalcode'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.venue_postalcode"
                :placeholder="$t('postal_code')"
                @valuechanged="onVenueTextFieldValueChange"
                @validated="onVenueDetailsValidation"
                :required="bookingLocationSettingsMandatory('Postalcode')"
                :showValidationIcons="
                  bookingLocationSettingsMandatory('Postalcode')
                "
                immediateValidation
              />
              <Dropdown
                v-if="bookingLocationSettings('Country')"
                class="m2"
                box
                v-bind="requiredDropdownConfig"
                :placeholder="$t('country')"
                :options="countriesTV"
                :selected="
                  countriesTV.find(
                    (e) => e.value == bookingRequest.venue_country
                  )
                "
                @selectedChange="(e) => onCountryChange('venue_country', e)"
                @validated="(e) => onContactValidation('venue_country', e)"
                :required="bookingLocationSettingsMandatory('Country')"
                :showValidationIcons="
                  bookingLocationSettingsMandatory('Country')
                "
                immediateValidation
              ></Dropdown>
              <TextField
                v-if="bookingLocationSettings('Phone1')"
                :id="'venue_phone1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.venue_phone1"
                :placeholder="$t('phone')"
                @valuechanged="onVenueTextFieldValueChange"
                @validated="onVenueDetailsValidation"
                :required="bookingLocationSettingsMandatory('Phone1')"
                :showValidationIcons="
                  bookingLocationSettingsMandatory('Phone1')
                "
                immediateValidation
              />
              <TextField
                v-if="bookingLocationSettings('Url')"
                :id="'venue_url1'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.venue_url1"
                :placeholder="$t('url')"
                @valuechanged="onVenueTextFieldValueChange"
                @validated="onVenueDetailsValidation"
                :required="bookingLocationSettingsMandatory('Url')"
                :showValidationIcons="bookingLocationSettingsMandatory('Url')"
                immediateValidation
              />
              <TextField
                v-if="bookingLocationSettings('Capacity')"
                :id="'venue_capacity'"
                class="m1"
                v-bind="textFieldConfig"
                :value="bookingRequest.venue_capacity"
                :placeholder="$t('capacity')"
                @valuechanged="onVenueTextFieldValueChange"
                @validated="onVenueDetailsValidation"
                :required="bookingLocationSettingsMandatory('Capacity')"
                :showValidationIcons="
                  bookingLocationSettingsMandatory('Capacity')
                "
                immediateValidation
              />

              <div
                v-for="venueField of bookingRequest.booking_location_cf"
                :key="venueField.field_label || venueField.custom_field_id"
              >
                <TextField
                  v-if="bookingLocationSettings(venueField.field_label)"
                  :id="venueField.field_label"
                  class="m1"
                  v-bind="textFieldConfig"
                  :value="venueField.data"
                  :placeholder="`${$dft.capSnake(venueField.field_label)}`"
                  @valuechanged="
                    (name, value) =>
                      onVenueFieldsUpdate(name, value, venueField)
                  "
                  @validated="
                    (name, value) =>
                      onVenueFieldValidation(name, value, venueField)
                  "
                  :required="
                    bookingLocationSettingsMandatory(venueField.field_label)
                  "
                  :showValidationIcons="
                    bookingLocationSettingsMandatory(venueField.field_label)
                  "
                  immediateValidation
                />
              </div>
            </div>
          </BasicAccordion>
        </div>
      </div>

      <footer class="exform-footer footer-booking-request">
        <div class="footer-content">
          <span
            >By clicking to submit this form, you acknowledge that the
            information you provide will be transferred to DETAILS for
            processing in accordance with their
            <a href="#">Privacy Policy</a> and GDPR-compliant
            <a href="#">Terms</a>.</span
          >
        </div>

        <div class="powered-by ut-flex">
          <span>Powered by</span>
          <div class="logo-box">
            <img
              class="img-responsive company-logo"
              :src="`https://detailss3web.s3.amazonaws.com/images/logo/big_fat_demo_agencylogo`"
              alt
            />
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { clientName } from "../../../services/http/http-client";

import * as I18nActionTypes from "../../../store/app/i18n/types";

import InputField from "@plugins/text-field/input-field";
import BasicAccordion from "@plugins/basic-accordion/basic-accordion";
import ExternalFormHeader from "../external-form/header.vue";

import SwitchButton from "./switch-button";

import { EventHooks } from "../../../plugins/text-field";
import { format } from "date-fns";
import { BookingRequestService } from "../../../services/booking-request.service";
import { createTV } from "../../../models/text-value.model";
import { subscribeSubject, wait } from "../../../utility";
import { Subject } from "rxjs";
import { flatten, omit, pick, keys } from "ramda";
import {
  NotificationService,
  showError,
  showNotification,
} from "../../../dt-plugins/notification/notification.service";

export default {
  components: {
    ExternalFormHeader,
    InputField,

    BasicAccordion,
    SwitchButton,
  },
  data: function () {
    return {
      clientName: clientName,
      stickyValue: false,

      openedState: true,
      event_detailsExpandState: false,
      contactExpandState: false,
      contract_partner_detailsExpandState: false,
      location_detailsExpandState: false,
      artist_detailsExpandState: false,

      switchValue: true,
      submitButtonFlag: false,

      textFieldConfig: {
        height: 42,
        showEditIconOnHover: false,
        box: true,
        showValidationIcons: false,
        validationEvent: EventHooks.OnBlur,
        valueChangedEvent: EventHooks.OnBlur,
      },
      textAreaConfig: {
        height: 120,
        showEditIconOnHover: false,
        box: true,
        textarea: true,
        showValidationIcons: false,
      },

      requiredDropdownConfig: {
        required: true,
        immediateValidation: true,
        showValidationIcons: true,
        validationEvent: EventHooks.OnBlur,
        valueChangedEvent: EventHooks.OnBlur,
      },

      typeAheadTextFieldConfig: {
        height: 42,
        showEditIconOnHover: false,
        box: true,
        showValidationIcons: false,
        validationEvent: EventHooks.OnKeyup,
        valueChangedEvent: EventHooks.OnKeyup,
        typeahead: true,
      },

      eventDetailsValidationResult: {},
      artistDetailsValidationResult: {},
      contactValidationResult: {},
      promoterValidationResult: {},
      venueValidationResult: {},

      booking_start_date: format(new Date(), "YYYY-MM-DD"),
      bookingRequest: this.get_bookingRequest(),
      // bookingRequest: null,

      language: "en",

      venue: "",
      venue_address1: "",
      venue_city: "",
      venueSubject: new Subject(),
      venueList: [],
      venueTV: [],
      selectedVenue: null,
    };
  },
  mounted: async function () {
    this.$store.dispatch(I18nActionTypes.UPDATE_SYSTEM_LANGUAGE, {
      locale: "en",
      language: "English",
    });

    const res = await BookingRequestService.getData(this.artist_id);

    this.bookingRequest = {
      ...this.bookingRequest,
      ...res.data,
    };
    console.log("this.bookingRequest = ", this.bookingRequest);

    console.log("this.artistsTV = ", this.artistsTV);

    subscribeSubject(this, this.venueSubject, (searchString) => {
      if (searchString.length > 0) {
        this.fetchVenues(searchString);
      }
    });

    // window.addEventListener("scroll", function() {
    //   let container = document.querySelector(".container");
    //   container.classList.toggle(
    //     "sticky",
    //     window.scrollY > container.offsetTop
    //   );
    // });

    window.addEventListener("scroll", function () {
      let header = document.querySelector(".header");
      header.classList.toggle("sticky", window.scrollY > 0);
    });
  },
  computed: {
    artist_id: function () {
      return this.$route.query.artist_id || null;
    },

    saveBookingRequest: function () {
      // let newArtist = [];
      // this.artists.forEach(e => {
      //   newArtist.push({
      //     artist_name: e.artist_name,
      //     artist_id: e.artist_id,
      //     first_offer_notes: e.first_offer_notes,
      //     performance_time: e.performance_time
      //   });
      // });

      let newArtist = this.bookingRequest.artistsList.map((e) => {
        // console.log('newArtist ', );
        return {
          artist_name: e.artist_name,
          artist_id: e.artist_id,
          first_offer_notes: e.first_offer_notes,
          performance_time: e.performance_time,
          performance_time_contract: e.performance_time_contract,
          artist_soundcheck: e.artist_soundcheck,
          artist_before: e.artist_before,
          artist_after: e.artist_after,
          artist_stage_floor: e.artist_stage_floor,
        };
      });

      return {
        booking_start_date: this.bookingRequest?.booking_start_date,
        booking_date_name: this.bookingRequest?.booking_date_name,
        booking_event_url: this.bookingRequest?.booking_event_url,
        booking_lineup: this.bookingRequest?.booking_lineup,
        booking_doors_open: this.bookingRequest?.booking_doors_open,
        booking_curfew: this.bookingRequest?.booking_curfew,
        // Contact
        contact_first_name: this.bookingRequest?.contact_first_name,
        contact_last_name: this.bookingRequest?.contact_last_name,
        contact_address1: this.bookingRequest?.contact_address1,
        contact_address2: this.bookingRequest?.contact_address2,
        contact_city: this.bookingRequest?.contact_city,
        contact_postalcode: this.bookingRequest?.contact_postalcode,
        contact_country: this.bookingRequest?.contact_country,
        contact_phone1: this.bookingRequest?.contact_phone1,
        contact_phone2: this.bookingRequest?.contact_phone2,
        contact_email1: this.bookingRequest?.contact_email1,
        contact_fax1: this.bookingRequest?.contact_fax1,
        contact_chat3: this.bookingRequest?.contact_chat3,
        // Promoter
        promoter_company_name: this.bookingRequest?.promoter_company_name,
        promoter_company_legalname:
          this.bookingRequest?.promoter_company_legalname,
        promoter_address1: this.bookingRequest?.promoter_address1,
        promoter_address2: this.bookingRequest?.promoter_address2,
        promoter_city: this.bookingRequest?.promoter_city,
        promoter_postalcode: this.bookingRequest?.promoter_postalcode,
        promoter_country: this.bookingRequest?.promoter_country,
        promoter_phone1: this.bookingRequest?.promoter_phone1,
        promoter_phone2: this.bookingRequest?.promoter_phone2,
        promoter_email1: this.bookingRequest?.promoter_email1,
        promoter_vat_no: this.bookingRequest?.promoter_vat_no,
        promoter_tax_no: this.bookingRequest?.promoter_tax_no,
        booking_location_id: this.selectedVenue?.booking_location_id,
        // venue
        venue_name: this.bookingRequest?.venue_name,
        venue_address1: this.bookingRequest?.venue_address1,
        venue_address2: this.bookingRequest?.venue_address2,
        venue_city: this.bookingRequest?.venue_city,
        venue_postalcode: this.bookingRequest?.venue_postalcode,
        venue_country: this.bookingRequest?.venue_country,
        venue_phone1: this.bookingRequest?.venue_phone1,
        venue_url1: this.bookingRequest?.venue_url1,
        venue_capacity: this.bookingRequest?.venue_capacity,
        request: this.bookingRequest?.request,
        artists: JSON.stringify(newArtist),
      };
    },

    languagesTV: function () {
      return this.$store.getters.systemLanguagesTV;
    },

    artistsTV: function () {
      return createTV(
        this.bookingRequest?.artists?.filter((artist) => {
          return artist.artist_type == "roster_artist";
        }),
        "artist_name",
        "artist_id"
      );
    },

    countriesTV: function () {
      return createTV(this.bookingRequest?.countries);
    },

    artistsLength: function () {
      // console.log("artistsLength ", this.bookingRequest.artistsList.length || 0);
      return this.bookingRequest.artistsList.length || 0;
    },

    isFormValidEventDetails: function () {
      return Object.values(this.eventDetailsValidationResult).every(
        (e) => e.valid
      );
    },

    // isFormValidArtistDetails: function() {
    //   return Object.values(this.artistDetailsValidationResult).every(
    //     e => e.valid
    //   );
    // },
    isFormValidArtistDetails: function () {
      return flatten(
        Object.values(this.artistDetailsValidationResult).map((e) =>
          Object.values(e)
        )
      ).every((e) => e.valid);
    },

    isFormValidContact: function () {
      return Object.values(this.contactValidationResult).every((e) => e.valid);
    },
    isFormValidPromoter: function () {
      return Object.values(this.promoterValidationResult).every((e) => e.valid);
    },
    isFormValidVenue: function () {
      return Object.values(this.venueValidationResult).every((e) => e.valid);
    },

    isValidComplete: function () {
      return (
        this.isFormValidEventDetails &&
        this.isFormValidArtistDetails &&
        this.isFormValidContact &&
        this.isFormValidPromoter &&
        this.isFormValidVenue
      );
    },

    isArtistAccordionValidated() {
      const isAllArtistsValid = Object.values(
        this.artistDetailsValidationResult
      ).every((e) => Object.values(e)?.every((d) => d.valid));
      console.log(
        "this.artistDetailsValidationResult",
        this.artistDetailsValidationResult
      );
      console.warn("isAllArtistsValid", isAllArtistsValid);
      return isAllArtistsValid;
    },
  },
  methods: {
    onContainerScroll: function (event) {
      if (event.target.scrollTop > 0) {
        this.stickyValue = true;
      } else if (event.target.scrollTop == 0) {
        this.stickyValue = false;
      }
    },

    onShowAccordion: function (accordionKey, value) {
      // console.log("Accordion key ", accordionKey, value);
      switch (accordionKey) {
        case "event_details":
          if (this.openedState == true) {
            this.event_detailsExpandState = false;
            this.openedState = false;
          } else {
            this.event_detailsExpandState = !value;
          }
          break;
        case "artist_details":
          this.artist_detailsExpandState = !value;
          break;
        case "contact":
          this.contactExpandState = !value;
          break;
        case "contract_partner_details":
          this.contract_partner_detailsExpandState = !value;
          break;
        case "location_details":
          this.location_detailsExpandState = !value;
          break;
        default:
          break;
      }
    },

    onChangeSwitchValue: function () {
      this.switchValue = !this.switchValue;
    },

    fetchVenues: async function (term) {
      const res = await BookingRequestService.findLocation(term);

      console.log("fetchVenues ", res.data);
      this.venueList = res.data;
      console.log("this.venueList ", this.venueList);

      this.venueTV = createTV(
        res.data || [],
        "booking_location_name",
        "booking_location_id"
      );
    },

    onVenueSelected: function (selected) {
      this.selectedVenue = this.venueList.find(
        (e) => e.booking_location_id == selected.value
      );

      if (this.selectedVenue) {
        this.bookingRequest.venue_name =
          this.selectedVenue.booking_location_name;
        this.bookingRequest.venue_address1 = this.selectedVenue.address1;
        this.bookingRequest.venue_address2 = this.selectedVenue.address2;
        this.bookingRequest.venue_city = this.selectedVenue.city;
        this.bookingRequest.venue_postalcode = this.selectedVenue.postalcode;
        this.bookingRequest.venue_country = this.selectedVenue.country;
        this.bookingRequest.venue_phone1 = this.selectedVenue.phone1;
        this.bookingRequest.venue_url1 = this.selectedVenue.url1;
        this.bookingRequest.venue_capacity = this.selectedVenue.venue_capacity;
      }
    },

    onVenueTextFieldValueChange: function (name, value) {
      // console.log("onVenue TextField ValueChange ", name, value);
      this.bookingRequest = {
        ...this.bookingRequest,
        [name]: value,
      };
      this.venueSubject.next(value);
    },

    onVenueFieldsUpdate: function (name, value, field) {
      console.log("onVenueFields Update ", name, value, field);
      this.bookingRequest = {
        ...this.bookingRequest,
        booking_location_cf: this.bookingRequest?.booking_location_cf.map(
          (e) => {
            if (e.custom_field_id == field.custom_field_id) {
              return {
                ...e,
                data: value,
              };
            } else {
              return e;
            }
          }
        ),
      };

      // console.log(
      //   "this.bookingRequest?.booking_location_cf update_2",
      //   this.bookingRequest?.booking_location_cf
      // );
    },

    onPromoterTextFieldValueChange: function (name, value) {
      this.bookingRequest = {
        ...this.bookingRequest,
        [name]: value,
      };

      console.log("Promoter update ", this.bookingRequest);
    },

    onPromoterFieldsUpdate: function (name, value, field) {
      console.log("onPromoterFields Update ", name, value, field);
      this.bookingRequest = {
        ...this.bookingRequest,
        promoter_cf: this.bookingRequest?.promoter_cf.map((e) => {
          if (e.custom_field_id == field.custom_field_id) {
            return {
              ...e,
              [name]: value,
            };
          } else {
            return e;
          }
        }),
      };
    },

    onEventDetailsTextFieldValueChange: function (name, value) {
      this.bookingRequest = {
        ...this.bookingRequest,
        [name]: value,
      };
    },

    onEventFieldsUpdate: function (name, value, field) {
      // console.log("onVenueFields Update ", name, value, field);
      this.bookingRequest = {
        ...this.bookingRequest,
        calendar_date_cf: this.bookingRequest?.calendar_date_cf.map((e) => {
          if (e.custom_field_id == field.custom_field_id) {
            return {
              ...e,
              data: value,
            };
          } else {
            return e;
          }
        }),
      };
    },

    onContactTextFieldValueChange: function (name, value) {
      this.bookingRequest = {
        ...this.bookingRequest,
        [name]: value,
      };
    },

    onContactFieldsUpdate: function (name, value, field) {
      // console.log("onContactFields Update ", name, value, field);
      this.bookingRequest = {
        ...this.bookingRequest,
        contact_cf: this.bookingRequest?.contact_cf.map((e) => {
          if (e.custom_field_id == field.custom_field_id) {
            return {
              ...e,
              data: value,
            };
          } else {
            return e;
          }
        }),
      };

      // console.log(
      //   "this.bookingRequest?.contact_cf update_2",
      //   this.bookingRequest?.contact_cf
      // );
    },

    // on2DropdownChange: function(name, { text, value }, index) {
    //   console.log("on2DropdownChange ", name, text, value, index);

    //   let element = this.bookingRequest.artistsList[index];
    //   element.artist_id = value;
    //   element[name] = text;
    // },

    onCountryChange: function (name, value) {
      console.log("countriesTV ", this.countriesTV);
      console.log("Country change ", name, value);

      // this[name] = value.value;
      this.bookingRequest = {
        ...this.bookingRequest,
        [name]: value.value,
      };
    },

    onLanguageChange: function (e) {
      console.log("onLanguageChange ", e);
      this.language = e.value;
      this.$store.dispatch(I18nActionTypes.UPDATE_SYSTEM_LANGUAGE, e);
    },

    onDateChange: function (name, value) {
      console.log("onDateChange ", name, value);
      this.bookingRequest = {
        ...this.bookingRequest,
        [name]: value,
      };
    },

    onEventDetailsValidation: function (name, value) {
      this.$set(this.eventDetailsValidationResult, name, value);
    },

    onEventFieldValidation: function (name, value) {
      this.$set(this.eventDetailsValidationResult, name, value);
    },

    onAddArtist: function () {
      console.log("artistsLength ", this.artistsLength);

      // this.bookingRequest.artistsList.push({
      //   artist_id: this.artistsLength,
      //   index: new Date().getTime()
      // });

      // artists	"[{\"artist_name\":\"Aayla+Secura\",\"artist_id\":\"5\",\"first_offer_notes\":\"\",\"performance_time\":\"\",\"performance_time_contract\":\"\",\"artist_soundcheck\":\"\",\"artist_before\":\"\",\"artist_after\":\"\",\"artist_stage_floor\":\"\"}]"

      this.bookingRequest = {
        ...this.bookingRequest,
        artistsList: [
          ...this.bookingRequest.artistsList,
          {
            artist_id: new Date().getTime(),
            first_offer_notes: "",
            performance_time: "",
            performance_time_contract: "",
            artist_soundcheck: "",
            artist_before: "",
            artist_after: "",
            artist_stage_floor: "",
          },
        ],
      };

      console.log("add artists ", this.bookingRequest);
    },

    onDeleteArtist: function (index, artist) {
      // console.log("artist deleted ", index, artist);
      // this.bookingRequest.artistsList.splice(index, 1);

      if (this.bookingRequest.artistsList.length == 1) return;

      this.bookingRequest = {
        ...this.bookingRequest,
        artistsList: this.bookingRequest.artistsList.filter(
          (e, i) => index != i
        ),
      };
      this.artistDetailsValidationResult =
        delete this.artistDetailsValidationResult[index];
    },

    onArtistTextFieldValueChange: function (name, value, index) {
      console.log("onArtistTextFieldValueChange ", name, value, index);

      this.bookingRequest = {
        ...this.bookingRequest,
        artistsList: this.bookingRequest?.artistsList.map((e, i) => {
          if (i == index) {
            return {
              ...e,
              [name]: value,
            };
          } else {
            return e;
          }
        }),
      };
      console.log("onTextField Artists Value update ", this.bookingRequest);
    },

    onArtistFieldsUpdate: function (name, value, field) {
      // console.log("onContactFields Update ", name, value, field);
      this.bookingRequest = {
        ...this.bookingRequest,
        calendar_date_artist_cf:
          this.bookingRequest?.calendar_date_artist_cf.map((e) => {
            if (e.custom_field_id == field.custom_field_id) {
              return {
                ...e,
                data: value,
              };
            } else {
              return e;
            }
          }),
      };
    },

    onArtistDropdownValidation(name, validationResult, index, artist) {
      // console.log(
      //   "Artist Dropdown Validation -- ",
      //   name,
      //   validationResult?.valid,
      //   index,
      //   artist?.artist_id
      // );

      // this.$set(this.artistDetailsValidationResult, name, value);

      console.log(
        `
        Before Artist Dropdown Validation -- 
        ValidationResult
        ${keys(this.artistDetailsValidationResult)}
        Artist:
        ${artist.artist_id}
        ArtistList: 
        ${this.bookingRequest.artistsList?.map((e) => e.artist_id)}
        `,

        validationResult,
        this.artistDetailsValidationResult
      );

      this.artistDetailsValidationResult = {
        // ...(pick(
        //   this.bookingRequest.artistsList?.map((e) => e.artist_id),
        //   this.artistDetailsValidationResult
        // ) || {}),
        ...this.artistDetailsValidationResult,
        [index]: {
          ...(this.artistDetailsValidationResult?.[index] || {}),
          [name]: validationResult,
        },
      };

      console.log(
        `
        After Artist Dropdown Validation -- 
        ValidationResult
        ${keys(this.artistDetailsValidationResult)}
        Artist:
        ${artist.artist_id}
        ArtistList: 
        ${this.bookingRequest.artistsList?.map((e) => e.artist_id)}
        `,
        validationResult,
        this.artistDetailsValidationResult
      );
    },
    onArtistSelection: function (value, artist, index) {
      console.log("onArtistSelection ", value, artist, index);

      // console.log(
      //   `
      //   on Artist Change - Artist List:  ${this.bookingRequest?.artistsList?.map(
      //     (e) => [e.artist_id, e.artist_name]
      //   )}..... ValidationResult: ${keys(this.artistDetailsValidationResult)}
      //   this.artistsTV: ${this.artistsTV?.map((e) => [e.value, e.text])}
      //   `
      // );

      const artist_id = value.value;

      const artist_name =
        this.artistsTV?.find((e) => e.value == artist_id)?.text || "";
      console.log({ "booking-request.vue:2097 artist_name": artist_name });

      this.bookingRequest = {
        ...this.bookingRequest,
        artistsList: this.bookingRequest?.artistsList.map((ar, i) => {
          if (i == index) {
            return {
              ...ar,
              artist_id,
              artist_name,
            };
          } else {
            return ar;
          }
        }),
      };

      // Clearing the Artist Validation Result from the previous artist_id key using ramda

      // console.log(
      //   "booking-request.vue:2082 this.artistDetailsValidationResult",
      //   keys(this.artistDetailsValidationResult)
      //   // this.artistDetailsValidationResult
      // );
      // this.artistDetailsValidationResult = {
      //   ...this.artistDetailsValidationResult,
      //   [value.value]: this.artistDetailsValidationResult[artist.artist_id],
      // };
      // this.artistDetailsValidationResult = omit(
      //   [artist.artist_id],
      //   this.artistDetailsValidationResult
      // );

      console.log(
        `After Artist Change - Artist List:  ${this.bookingRequest?.artistsList?.map(
          (e) => [e.artist_id, e.artist_name]
        )}..... ValidationResult: ${keys(this.artistDetailsValidationResult)}`
      );

      // console.log(
      //   "on Dropdown Change Artists update ",

      //   "this.artistDetailsValidationResult",
      //   keys(this.artistDetailsValidationResult),
      //   // this.artistDetailsValidationResult
      // );
    },

    onArtistTextFieldValidation(name, validationResult, index, artist) {
      console.log(
        "Artist TextField Validation -- ",
        name,
        validationResult,
        index,
        artist
      );

      // this.$set(this.artistDetailsValidationResult, name, validationResult);

      this.artistDetailsValidationResult = {
        ...this.artistDetailsValidationResult,
        [index]: {
          ...this.artistDetailsValidationResult[index],
          [name]: validationResult,
        },
      };

      console.log(
        "this.artistDetailsValidationResult",
        this.artistDetailsValidationResult
      );
    },

    onArtistFieldValidation: function (name, value, index) {
      // this.$set(this.artistDetailsValidationResult, name, value);
      this.artistDetailsValidationResult = {
        ...this.artistDetailsValidationResult,
        [index]: {
          ...this.artistDetailsValidationResult[index],
          [name]: value,
        },
      };
    },

    onContactValidation: function (name, value) {
      this.$set(this.contactValidationResult, name, value);
    },
    onContactFieldValidation: function (name, value, contactField) {
      // console.log("Contact ", name, value, contactField);
      this.$set(this.contactValidationResult, name, value);

      this.contactValidationResult = {
        ...this.contactValidationResult,
        [name]: value,
      };
    },

    onPromoterDetailsValidation: function (name, value) {
      this.$set(this.promoterValidationResult, name, value);
    },

    onPromoterFieldValidation: function (name, value, promoterField) {
      // console.log("Promoter ", name, value, promoterField);
      this.$set(this.promoterValidationResult, name, value);

      this.promoterValidationResult = {
        ...this.promoterValidationResult,
        [name]: value,
      };
    },

    onVenueDetailsValidation: function (name, value) {
      this.$set(this.venueValidationResult, name, value);
    },

    onVenueFieldValidation: function (name, value, venueField) {
      // console.log("Venue ", name, value, venueField);
      this.$set(this.venueValidationResult, name, value);

      this.venueValidationResult = {
        ...this.venueValidationResult,
        [name]: value,
      };
      const valid = Object.values(this.venueValidationResult).every(
        (e) => e.valid
      );
      if (valid) {
        this.validationIconVenue = "textbox-complete.svg";
      } else {
        this.validationIconVenue = "textbox-incomplete.svg";
      }
    },

    get_bookingRequest: function () {
      return {
        // Event Details
        booking_start_date: "",
        booking_date_name: "",
        booking_event_url: "",
        booking_lineup: "",
        booking_doors_open: "",
        booking_curfew: "",
        // Contact
        contact_first_name: "",
        contact_last_name: "",
        contact_address1: "",
        contact_address2: "",
        contact_city: "",
        contact_postalcode: "",
        contact_country: "",
        contact_phone1: "",
        contact_phone2: "",
        contact_email1: "",
        contact_fax1: "",
        contact_chat3: "",
        // Promoter
        promoter_company_name: "",
        promoter_company_legalname: "",
        promoter_address1: "",
        promoter_address2: "",
        promoter_city: "",
        promoter_postalcode: "",
        promoter_country: "",
        promoter_phone1: "",
        promoter_phone2: "",
        promoter_email1: "",
        promoter_vat_no: "",
        promoter_tax_no: "",
        //
        booking_location_id: "",

        // Venue
        venue_name: "",
        venue_address1: "",
        venue_address2: "",
        venue_city: "",
        venue_postalcode: "",
        venue_country: "",
        venue_phone1: "",
        venue_url1: "",
        venue_capacity: "",
        request: "",
        // Artist Details
        artistsList: [
          {
            artist_id: new Date().getTime(),
            first_offer_notes: "",
            performance_time: "",
            performance_time_contract: "",
            artist_soundcheck: "",
            artist_before: "",
            artist_after: "",
            artist_stage_floor: "",
          },
        ],
      };
    },

    onBookingRequestSubmit: async function () {
      if (!this.isValidComplete) {
        showError("Please fill up all required information");

        if (
          !(
            this.event_detailsExpandState &&
            this.artist_detailsExpandState &&
            this.contactExpandState &&
            this.contract_partner_detailsExpandState &&
            this.location_detailsExpandState
          )
        ) {
          await wait(1000);

          showNotification("All Panels are opened");

          this.event_detailsExpandState = true;
          this.artist_detailsExpandState = true;
          this.contactExpandState = true;
          this.contract_partner_detailsExpandState = true;
          this.location_detailsExpandState = true;
        }
      } else {
        const res = await BookingRequestService.submitBookingRequest(
          this.saveBookingRequest
        );
        showNotification("Your data has succesfully been transferred");
        this.submitButtonFlag = true;
      }
    },

    // Booking Settings Methods
    is1(value) {
      return value == 1 || value == "1";
    },
    calendarDateSettings(keyName = "Event Name") {
      return this.is1(
        this.bookingRequest?.rs?.calendar_date?.[keyName]?.request
      );
    },
    calendarDateSettingsMandatory(keyName = "Event Name") {
      return this.is1(
        this.bookingRequest?.rs?.calendar_date?.[keyName]?.mandatory
      );
    },

    calendarDateArtistSettings(keyName = "Event Name") {
      return this.is1(
        this.bookingRequest?.rs?.calendar_date_artist?.[keyName]?.request
      );
    },
    calendarDateArtistSettingsMandatory(keyName = "Event Name") {
      return this.is1(
        this.bookingRequest?.rs?.calendar_date_artist?.[keyName]?.mandatory
      );
    },

    contactSettings(keyName = "Event Name") {
      return this.is1(this.bookingRequest?.rs?.contact?.[keyName]?.request);
    },
    contactSettingsMandatory(keyName = "Event Name") {
      return this.is1(this.bookingRequest?.rs?.contact?.[keyName]?.mandatory);
    },

    promoterSettings(keyName = "Event Name") {
      return this.is1(this.bookingRequest?.rs?.promoter?.[keyName]?.request);
    },
    promoterSettingsMandatory(keyName = "Event Name") {
      return this.is1(this.bookingRequest?.rs?.promoter?.[keyName]?.mandatory);
    },

    bookingLocationSettings(keyName = "Event Name") {
      return this.is1(
        this.bookingRequest?.rs?.booking_location?.[keyName]?.request
      );
    },
    bookingLocationSettingsMandatory(keyName = "Event Name") {
      return this.is1(
        this.bookingRequest?.rs?.booking_location?.[keyName]?.mandatory
      );
    },

    //MARK: Methods
  },
};
</script>

<style lang="scss">
// .cmp-booking-requesta {
//   --bc-accent5: #0087ff;
//   height: 100vh;
//   overflow: hidden;
//   background-color: #f6f6f6;

//   &.dark {
//     background-color: var(--bc-darker);

//     .basic-accordion {
//       border-color: transparent;
//       box-shadow: none;
//       .accordion-header {
//         .arrow {
//           border-color: var(--text-lightest);
//         }
//       }
//       .accordion-header,
//       .accordion-body {
//         background-color: #393939;
//         color: var(--text-lightest);
//         input,
//         textarea,
//         .dropdown--main__text {
//           color: var(--text-lightest);
//         }
//         .dt-input-box--label.input,
//         .dt-input-box--label.textarea,
//         .dropdown--main__label {
//           color: var(--text-5);
//         }
//         .dt-input-box.disabled:not(.readonly) {
//           background-color: transparent;
//         }
//       }
//     }
//     .expand {
//       box-shadow: 0 4px 10px transparent;
//       .basic-accordion {
//         border-color: transparent;
//       }
//     }

//     .page-header-inner {
//       background-color: var(--bc-darker);
//     }
//   }

//   .header {
//     position: fixed;
//     top: 0;
//     left: 0;
//     right: 0;
//     z-index: 99;
//     transition: all 0.6s ease-in-out;
//     &.sticky {
//       .top-bar {
//         height: 52px;
//         transition: all 0.6s ease-in-out;
//         .logo-box {
//           height: 100%;
//           .company-logo {
//             // max-width: 32px;
//             max-height: 32px;
//           }
//         }
//       }
//     }
//   }
//   .top-bar {
//     width: 100%;
//     height: 120px;
//     padding: 10px 0;
//     background-color: var(--bc-darker);
//     transition: all 0.6s ease-in-out;
//     .top-bar-inner {
//       display: flex;
//       width: 100%;
//       height: 100%;
//       padding: 0 40px;
//       @media (min-width: 1200px) {
//         max-width: 1400px;
//         margin-right: auto;
//         margin-left: auto;
//       }
//     }

//     .logo {
//       flex: 3;
//       transition: all 0.6s ease-in-out;

//       .company-logo {
//         display: block;
//         // width: 100%;
//         // max-width: 100px;
//         max-height: 100px;
//         transition: all 0.6s ease-in-out;
//         @media (max-width: 767px) {
//           max-height: 32px;
//         }
//       }
//     }

//     .logo-box {
//       height: 100%;
//       // max-width: 100px;
//     }

//     .language-box {
//       flex: 1;
//       max-width: 93px;
//       padding-top: 5px;
//       transition: all 0.6s ease-in-out;

//       .dropdown {
//         border: 1px solid var(--text-lightest);
//         z-index: 99;
//         border-radius: 3px;
//       }

//       .dropdown--main__text {
//         color: var(--text-lightest);
//       }
//       .dropdown--main {
//         padding: 0 0 0 5px;
//         .dropdown--main__icon {
//           padding-right: 0;
//           position: relative;
//           &:before {
//             content: "";
//             position: absolute;
//             top: 0;
//             right: 14px;
//             width: 1px;
//             height: 20px;
//             background-color: var(--text-lightest);
//           }
//           &:after {
//             content: "";
//             border: solid var(--text-lightest);
//             border-width: 0 1px 1px 0;
//             display: inline-block;
//             padding: 2px;
//             position: absolute;
//             top: 6px;
//             right: 4px;
//             transform: rotate(45deg);
//             -webkit-transform: rotate(45deg);
//           }
//         }
//         img.dt-icon.s1 {
//           display: none;
//         }
//       }
//     }
//   }

//   .page-header-inner {
//     padding: 0 40px;
//     margin-bottom: 20px;
//     background-color: #f6f6f6;
//     transition: all 0.6s linear;
//     @media (min-width: 1200px) {
//       width: 100%;
//       max-width: 1400px;
//       margin-right: auto;
//       margin-left: auto;
//     }
//   }

//   .page-header {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     height: 50px;
//     padding: 10px 0;
//     border-bottom: 1px solid var(--text-accent);

//     .buton-group {
//       display: flex;
//       transition: all 0.6s linear;
//       .dt-button {
//         width: 138px;
//         height: 30px;
//         border-color: var(--bc-accent5);
//         color: #fff;
//         background: var(--bc-accent5);
//         margin-right: 0;
//       }
//     }
//     @media (max-width: 767px) {
//       .page-titile {
//         font-size: 12px;
//       }
//     }
//   }

//   .page-titile {
//     font-size: 16px;
//     color: var(--text-accent);
//   }

//   // .top-bar.sticky + .container {
//   //   padding-top: 180px;
//   // }
//   .header.sticky + .container {
//     // padding-top: 150px;
//     max-height: calc(100vh - 122px);
//     margin-top: 122px;
//     // transition: all 0.6s linear;
//   }

//   .container {
//     max-height: calc(100vh - 190px);
//     // padding: 190px 40px 0;
//     padding: 0 40px 0;
//     overflow: hidden;
//     overflow-y: auto;
//     margin-top: 190px;
//     transition: (margin-top, max-height) 0.6s linear;
//     > .dt-box--row {
//       // min-height: 500px;
//       transition: all 0.6s linear;
//     }
//     @media (max-width: 1023px) {
//       .dt-box--row {
//         flex-direction: column;
//         flex-wrap: nowrap;
//         .dt-box--column {
//           flex-basis: 100% !important;
//           &:nth-child(2) {
//             .dt-modal--subtitle {
//               display: none;
//             }
//           }
//         }
//       }
//     }
//     @media (min-width: 1200px) {
//       max-width: 1400px;
//       .dt-box--row {
//         .dt-box--column {
//           max-width: 50%;
//         }
//       }
//     }
//   }

//   .dt-box--row {
//     display: flex;
//     flex-wrap: wrap;
//     width: calc(100% - -32px);
//     margin: 0 -8px;

//     .dt-box--column {
//       flex: 1;
//       padding: 0 8px;
//     }
//   }

//   .footer-booking-request {
//     margin: 80px 0;
//     .footer-content {
//       max-width: 665px;
//     }
//     .footer-content,
//     .powered-by {
//       span {
//         display: block;
//         line-height: 18px;
//         color: #828282;
//       }

//       a {
//         color: #0087ff;
//       }
//     }
//     .powered-by {
//       margin-top: 32px;
//       .logo-box {
//         margin-left: 8px;
//         .company-logo {
//           display: block;
//           width: 100%;
//           max-width: 82px;
//         }
//       }
//     }
//   }
// }
</style>

<style lang="scss" scoped>
.menu__content {
  border: none !important;
  box-shadow: none !important;
}
</style>
<!--
booking_start_date	"2022-02-22"
booking_date_name	""
booking_event_url	""
booking_lineup	""
booking_doors_open	""
booking_curfew	""
contact_first_name	"cont+1"
contact_last_name	"c2"
contact_address1	""
contact_address2	""
contact_city	""
contact_postalcode	""
contact_country	"Bangladesh"
contact_phone1	""
contact_phone2	""
contact_email1	""
contact_fax1	""
contact_chat3	""
promoter_company_name	"dada+giri"
promoter_company_legalname	""
promoter_address1	"fr4"
promoter_address2	"fr3"
promoter_city	""
promoter_postalcode	""
promoter_country	"Bangladesh"
promoter_phone1	""
promoter_phone2	""
promoter_email1	""
promoter_vat_no	""
promoter_tax_no	""
booking_location_id	"219954"
venue_name	"A+L'ARME!+Festival+Rastig+&+Mertin+GbR+-+ABOUTNOW"
venue_address1	"Kiefholzstrasse+16+"
venue_address2	"eestst"
venue_city	"Berlin"
venue_postalcode	"12435"
venue_country	"Germany"
venue_phone1	""
venue_url1	""
venue_capacity	""
artists	"[{\"artist_name\":\"Aayla+Secura\",\"artist_id\":\"5\",\"first_offer_notes\":\"\",\"performance_time\":\"\",\"performance_time_contract\":\"\",\"artist_soundcheck\":\"\",\"artist_before\":\"\",\"artist_after\":\"\",\"artist_stage_floor\":\"\"}]"
request	"1"
action	"booking_request_data_no_promotor"
 -->
